import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { environment } from './environments/environment';
import './app/core/opentelemetry';
import './app/core/load-js';
import { loadJs } from './app/core/load-js';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient } from '@angular/common/http';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { provideRouter, RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { AppTitlePrefix } from './app/app-title.prefix';
import { isPlatform } from '@ionic/core';
import {
  MatomoConsentMode,
  provideMatomo,
  withRouter,
  withRouterInterceptors,
} from 'ngx-matomo-client';
import { UrlFilterInterceptor } from './app/analytics/url-filter-interceptor.service';
import { PageTitleInterceptor } from './app/analytics/page-title.interceptor';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe } from '@angular/common';
import StatusService from './app/authentication/service/status.service';
import AuthService from './app/authentication/service/auth.service';
import { MeldingenService } from './app/profiel/service/meldingen.service';
import { PushNotificatieService } from './app/profiel/service/push-notificatie.service';
import { SucceservaringenService } from './app/succeservaring/succeservaringen.service';
import { VersioningService } from './app/versioning/versioning.service';
import { BiometricsConfigModalService } from './app/authentication/service/biometrics-config-modal.service';
import { NativeService } from './app/core/native.service';
import { BiometricsService } from './app/authentication/service/biometrics.service';
import { ActivityDetectionService } from './app/authentication/service/activity-detection.service';
import BackgroundDetectionService from './app/authentication/service/background-detection.service';
import { DeeplinkingService } from './app/core/config/deeplinking.service';
import { ContextService } from './app/shared/services/context.service';
import { LastContextService } from './app/shared/services/last-context.service';
import { distinctUntilChanged, map, switchMap } from 'rxjs';
import { GlobalErrorHandler } from './app/core/global-error-handler';
import { GlobalHttpErrorHandler } from './app/core/global-http-error-handler';
import { MakeReferencesAbsolutePipe } from './app/watwat/pipes/make-references-absolute.pipe';
import { MakeUrlAbsolutePipe } from './app/watwat/pipes/make-url-absolute.pipe';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { finalRoutes } from './app/routes';
import { IonicModule } from '@ionic/angular';
import { LongPressDirective } from './app/shared/directives/long-press-directive';
import { IsUserWithProfielDirective } from './app/shared/directives/is-user-with-profiel.directive';
import { AdaptToSidebarVisibleDirective } from './app/shared/directives/adapt-to-sidebar-visible.directive';
import { PageContentDirective } from './app/shared/directives/page-content.directive';

loadJs(environment.jitsiApiEmbedCode);

if (environment.production) {
  enableProdMode();
}

function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, '/assets/i18n/', '.json');
}

bootstrapApplication(AppComponent, {
  providers: [
    TranslatePipe,
    provideHttpClient(),
    importProvidersFrom(
      IonicModule.forRoot({}),
      TranslateModule.forRoot({
        defaultLanguage: 'nl',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    provideRouter(finalRoutes),
    AppTitlePrefix,
    {
      provide: TitleStrategy,
      useClass: AppTitlePrefix,
    },
    provideIonicAngular({
      animated: !isPlatform('desktop'),
    }),
    provideAnimations(),
    provideMatomo(
      {
        disabled: !environment.matomo.enabled,
        trackerUrl: environment.matomo.trackerUrl,
        siteId: environment.matomo.siteId,
        enableJSErrorTracking: true,
        requireConsent: MatomoConsentMode.COOKIE,
      },
      withRouterInterceptors([UrlFilterInterceptor, PageTitleInterceptor]),
      withRouter(),
    ),
    DatePipe,
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'dd.MM.yyyy' } },
    {
      provide: APP_INITIALIZER,
      useFactory: (statusService: StatusService) => () => {
        statusService.initialize();
      },
      deps: [StatusService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService) => () => {
        authService.loadInitialUser();
      },
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (meldingenService: MeldingenService) => () =>
        meldingenService.startMeldingenFetcher(),
      deps: [MeldingenService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (pushNotificatieService: PushNotificatieService) => () =>
        pushNotificatieService.initialize(),
      deps: [PushNotificatieService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (succeservaringenService: SucceservaringenService) => () =>
        succeservaringenService.initialize(),
      deps: [SucceservaringenService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (versioningService: VersioningService) => () =>
        versioningService.initializeVersionCheck(),
      deps: [VersioningService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (biometricsService: BiometricsConfigModalService) => () =>
        biometricsService.initialize(),
      deps: [BiometricsConfigModalService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (nativeService: NativeService) => () => nativeService.initialize(),
      deps: [NativeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (biometricsService: BiometricsService) => async () =>
        biometricsService.initialize(),
      deps: [BiometricsService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (activityDetectionService: ActivityDetectionService) => () =>
        activityDetectionService.initialize(),
      deps: [ActivityDetectionService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (backgroundDetectionService: BackgroundDetectionService) => () =>
        backgroundDetectionService.initialize(),
      deps: [BackgroundDetectionService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (deeplinkingConfig: DeeplinkingService) => () =>
        deeplinkingConfig.configureDeepLinking(),
      deps: [DeeplinkingService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (
          contextService: ContextService,
          authService: AuthService,
          lastContextService: LastContextService,
        ) =>
        () => {
          contextService.context$
            .pipe(
              distinctUntilChanged(),
              switchMap((context) => {
                return authService.getCurrentUser$().pipe(
                  map((user) => {
                    if (user.type === 'aangemeld') {
                      return {
                        persoonId: user.persoonId,
                        profielId: user.profielId,
                        contextId: context.contextId,
                      };
                    } else {
                      return undefined;
                    }
                  }),
                );
              }),
            )
            .subscribe((lastContext) => {
              if (lastContext) {
                lastContextService.addLastContext(lastContext).subscribe();
              }
            });
        },
      deps: [ContextService, AuthService, LastContextService],
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobalHttpErrorHandler,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'nl-BE',
    },
    MakeReferencesAbsolutePipe,
    MakeUrlAbsolutePipe,
    LongPressDirective,
    IsUserWithProfielDirective,
    AdaptToSidebarVisibleDirective,
    PageContentDirective,
  ],
}) // eslint-disable-next-line no-console
  .catch((err) => console.log(err));
